import React, { useState } from 'react';

import { Col, Dropdown, Modal, Row } from 'react-bootstrap';
import '../JobStageSummary.scss';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { CustomButton } from '../../../_common/CustomButton/CustomButton';
import {
    GetWellheadsByStageGroupId_getWellheadsByStageGroupId as StageWellheads,
    GetJobLogsDetails,
    GetJobLogsDetails_getJobLogsDetails as JobLogDetailsType,
    GetJobLogMetaData_getJobLogMetaData_types as JobLogTypesType,
    GetJobLogMetaData_getJobLogMetaData_issues as JobLogIssueType,
    GetStages_getStages as StageNumberType,
    GetJobLogMetaData_getJobLogMetaData_rootcauses as JobLogRootCauseType,
    GetJobLogMetaData_getJobLogMetaData_locationLOFAs as JobLogLocationLofaType,
} from '../../../../types/schemaTypes';
import { Loading } from '../../../_common';
import { ADD_EDIT_JOB_LOGS } from '../../../../graphql/client/mutations';
import { GET_JOBLOGS_BY_JOBID } from '../../../../graphql/client/queries';
import { PermissionEnum } from '../../../../types/PermissionEnum';
import { useAuthContext } from '../../../Auth/AuthProvider';

interface StartStageModalProps {
    showStartStageModal: boolean;
    stageNumbers: string | undefined;
    stageMsgNumbers: string | undefined;
    jobId: number;
    stageStatusData: any | undefined;
    jobLogRootCauses: JobLogRootCauseType[];
    jobLogLocationLofa: JobLogLocationLofaType[];
    handleOnClose: (modalType) => void;
    handleOnSave: (stageNumbers: unknown, jobId: unknown) => void;
    mutationLoading: boolean;
    modalError: string | undefined;
    setModalError: React.Dispatch<React.SetStateAction<string>>;
}

const StartStageModal = ({
    showStartStageModal,
    handleOnClose,
    handleOnSave,
    stageNumbers,
    stageMsgNumbers,
    jobId,
    stageStatusData,
    jobLogRootCauses,
    jobLogLocationLofa,
    mutationLoading,
    modalError,
    setModalError,
}: StartStageModalProps): React.ReactElement => {
    const authContext = useAuthContext();
    const isAddJobLogsEnabled = authContext.userHasPermission(PermissionEnum.AddJobLogs);

    const [logCount, setLogCount] = useState(0);

    const [wellheadStage, setWellheadStage] = useState<string>();
    const [jobStageIds, setJobStageIds] = useState<number[]>();

    const [logId, setLogId] = useState<number | null>(-1);
    const [logType, setLogType] = useState<string | null>('');
    const [logTypeId, setLogTypeId] = useState<number | null>(null);
    const [issueType, setIssueType] = useState<string | null>('');
    const [issueTypeId, setIssueTypeId] = useState<number | null>(null);
    const [jobStageId, setJobStageId] = useState<number | null>();
    const [stage, setStage] = useState<string | null>();
    const [downtime, setDowntime] = useState<number | null>(null);
    const [loadId, setLoadId] = useState<number | null>(null);
    const [lofa, setLofa] = useState<string | null>('');
    const [lofaId, setLofaId] = useState<number | null>(null);
    const [amount, setAmount] = useState<number | null>(null);
    const [rootCause, setRootCause] = useState<string | null>('');
    const [rootCauseId, setRootCauseId] = useState<number | null>(null);
    const [notes, setNotes] = useState<string | null>('');
    const [correctiveAction, setCorrectiveAction] = useState<string | null>('');

    const [downTimeError, setDownTimeError] = useState('');
    const [loadIdError, setLoadIdError] = useState('');
    const [amountError, setAmountError] = useState('');
    const [notesError, setNotesError] = useState('');

    const wellheadStageList = stageStatusData.stageStatus.filter(wellheadStageItem => {
        if (wellheadStageItem.status === 'Paused') return true;
        else return false;
    });

    const { loading: jobLogsLoading, error: jobLogsError, data: jobLogsData } = useQuery<GetJobLogsDetails>(
        GET_JOBLOGS_BY_JOBID,
        {
            variables: {
                jobId: jobId,
                tzinfo: encodeURIComponent(
                    new Date()
                        .toString()
                        .split('(')[1]
                        .replace(')', ''),
                ),
            },
            fetchPolicy: 'network-only',
        },
    );

    const jobLogs: JobLogDetailsType[] = (jobLogsData?.getJobLogsDetails ?? []).filter(
        (j): j is JobLogDetailsType => j != null,
    );
    if (jobLogsData?.getJobLogsDetails && jobLogs.length > 0) {
        if (logId === -1 && jobLogs.length > 0) {
            const log = jobLogs[0];
            setLogId(log.id);
            setLogType(log.jobLogType);
            setLogTypeId(log.jobLogTypeId);
            setIssueType(log.jobLogIssue);
            setIssueTypeId(log.jobLogIssueId);
            setDowntime(log.estimatedDowntime);
            setJobStageId(log.jobStageId);
            setStage(log.stage);
            setLoadId(log.loadId);
            setLofa(log.locationLofa);
            setAmount(log.amount);
            setRootCause(log.rootCause);
            setRootCauseId(log.rootCauseId);
            setNotes(log.notes);
            setCorrectiveAction(log.correctiveAction);
            setLogCount(1);
        }
    }

    const [addEditJobLog, { loading: addEditJobLogLoading }] = useMutation(ADD_EDIT_JOB_LOGS, {
        onError: error => {
            if (CombinedGraphQLErrors.is(error)) {
                setModalError(error.errors[0]?.message?.toString().replace(',', ' ') || error.message);
            } else {
                setModalError(error.message ?? error);
            }
        },
    });

    const handleOnChangeWellheadStage = (wellhead: string, jobStageId: number) => {
        if (wellhead === 'ALL') {
            const tempJobStageIds: number[] = [];
            let jobStageIds = '';
            wellheadStageList.map((WellheadStageItem, index) => {
                tempJobStageIds.push(WellheadStageItem.stageId);
                if (index === 0) jobStageIds = WellheadStageItem.stageId.toString();
                else jobStageIds += ',' + WellheadStageItem.stageId.toString();
            });
            setWellheadStage('Select All');
            setJobStageIds(tempJobStageIds);
        } else {
            setWellheadStage(`${wellhead}`);
            setJobStageIds([jobStageId]);
        }
    };

    const handleOnChangeDowntime = event => {
        setDownTimeError('');
        if (Number.isNaN(event.target.value)) setDowntime(null);
        else setDowntime(parseInt(event.target.value));
    };

    const handleOnChangeLoadId = event => {
        setLoadIdError('');
        if (Number.isNaN(event.target.value)) setLoadId(null);
        else setLoadId(parseInt(event.target.value));
    };

    const handleOnChangeLofa = (lofaId, lofa) => {
        setLofaId(lofaId);
        setLofa(lofa);
    };

    const handleOnChangeAmount = event => {
        setAmountError('');
        if (Number.isNaN(event.target.value)) setAmount(null);
        else setAmount(parseInt(event.target.value));
    };

    const handleOnChangeRootCause = (rootCauseId, rootCause) => {
        setRootCauseId(rootCauseId);
        setRootCause(rootCause);
    };

    const handleOnChangeCorrectiveAction = event => {
        setCorrectiveAction(event.target.value);
    };

    const handleOnChangeNotes = event => {
        setNotesError('');
        setNotes(event.target.value);
    };

    const onClickIgnore = () => {
        if (logCount !== jobLogs.length) {
            const log = jobLogs[logCount];
            setLogId(log.id);
            setLogType(log.jobLogType);
            setLogTypeId(log.jobLogTypeId);
            setIssueType(log.jobLogIssue);
            setIssueTypeId(log.jobLogIssueId);
            setDowntime(log.estimatedDowntime);
            setJobStageId(log.jobStageId);
            setStage(log.stage);
            setLoadId(log.loadId);
            setLofa(log.locationLofa);
            setAmount(log.amount);
            setRootCause(log.rootCause);
            setRootCauseId(log.rootCauseId);
            setNotes(log.notes);
            setCorrectiveAction(log.correctiveAction);
        }
        setLogCount(logCount + 1);
    };

    const onClickResolve = () => {
        let isSaveEnabled = true;

        if (downtime && downtime < 0) {
            isSaveEnabled = false;
            setDownTimeError('Enter a vaild number');
        }

        if (loadId && loadId < 0) {
            isSaveEnabled = false;
            setLoadIdError('Enter a vaild Load Id');
        }

        if (amount && amount < 0) {
            isSaveEnabled = false;
            setAmountError('Enter a vaild Amount');
        }

        if (notes && notes.length > 2000) {
            isSaveEnabled = false;
            setNotesError('Notes cannot be more than 2000 character');
        }

        if (isSaveEnabled) {
            addEditJobLog({
                variables: {
                    input: {
                        jobLogId: logId,
                        jobLogIssueId: issueTypeId,
                        jobLogTypeId: logTypeId,
                        jobId: jobId,
                        startDate: null,
                        endDate: new Date(),
                        estimatedDowntime: downtime,
                        jobStageId: jobStageId,
                        loadId: loadId,
                        notes: notes,
                        locationLofa: lofa,
                        amount: amount,
                        rootCauseId: rootCauseId,
                        correctiveAction: correctiveAction,
                    },
                },
            });

            if (logCount !== jobLogs.length) {
                const log = jobLogs[logCount];
                setLogId(log.id);
                setLogType(log.jobLogType);
                setLogTypeId(log.jobLogTypeId);
                setIssueType(log.jobLogIssue);
                setIssueTypeId(log.jobLogIssueId);
                setDowntime(log.estimatedDowntime);
                setJobStageId(log.jobStageId);
                setStage(log.stage);
                setLoadId(log.loadId);
                setLofa(log.locationLofa);
                setAmount(log.amount);
                setRootCause(log.rootCause);
                setRootCauseId(log.rootCauseId);
                setNotes(log.notes);
                setCorrectiveAction(log.correctiveAction);
            }
            setLogCount(logCount + 1);
        }
    };

    const onClickSave = () => {
        handleOnSave(stageNumbers, jobId);
    };

    const setContinueButtonStatus = (): boolean => {
        if (
            jobStageIds?.length === 0 ||
            !logTypeId ||
            (logTypeId !== 6 && !issueTypeId) ||
            mutationLoading ||
            addEditJobLogLoading
        ) {
            return true;
        }
        if (modalError === undefined || modalError === '') {
            return false;
        } else {
            return true;
        }
    };

    const onClickClose = () => {
        handleOnClose('startStage');
    };

    if (wellheadStageList.length === 1 && !wellheadStage) {
        handleOnChangeWellheadStage(wellheadStageList[0].wellHead, wellheadStageList[0].stageId);
    }

    if (wellheadStageList.length > 1 && !wellheadStage) {
        handleOnChangeWellheadStage('ALL', 0);
    }

    if (!jobLogsLoading && (jobLogs.length === 0 || logCount === jobLogs.length + 1)) {
        return (
            <Modal show={showStartStageModal} onHide={onClickClose} dialogClassName="StageModal-Container">
                {mutationLoading && (
                    <span className="AddStageModal-Loading">
                        <Loading />
                    </span>
                )}

                <Modal.Header className="AddStageModal-Header">
                    <Modal.Title className="AddStageModal-Header-Container">
                        <button className="material-icons AddStageModal-X-Button" onClick={onClickClose}>
                            close
                        </button>
                        <div className="AddStageModal-Title">Start Stage</div>
                        <div className="AddStageModal-Error">{modalError}</div>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="AddStageModal-Body">
                    <Col>
                        <div>Starting stage {stageMsgNumbers}. Please confirm to move forward.</div>
                    </Col>
                </Modal.Body>

                <Modal.Footer className="AddStageModal-Footer">
                    <CustomButton title="Cancel" isBordered onClick={onClickClose} />
                    <CustomButton title="Continue" onClick={() => onClickSave()} isDisabled={mutationLoading} />
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal show={showStartStageModal} onHide={onClickClose} dialogClassName="Pause-Modal-Container">
            {(jobLogsLoading || mutationLoading || addEditJobLogLoading) && (
                <span className="AddStageModal-Loading">
                    <Loading />
                </span>
            )}

            <Modal.Header className="AddStageModal-Header">
                <Modal.Title className="AddStageModal-Header-Container">
                    <button className="material-icons AddStageModal-X-Button" onClick={onClickClose}>
                        close
                    </button>
                    <div className="AddStageModal-Title">Active Job Events</div>
                    <div className="AddStageModal-Error">{modalError}</div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="Pause-Modal-Body">
                <Row className="Pause-Modal-Row-Container">
                    <Col md={5}>
                        <label className="Pause-Modal-Label">*Job Log Type</label>
                        <Dropdown className="Pause-Modal-Dropdown">
                            <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter" disabled={true}>
                                <span>{logType === '' ? 'Job Log Type' : logType}</span>
                            </Dropdown.Toggle>
                        </Dropdown>
                    </Col>

                    {logTypeId !== 6 ? (
                        <Col md={5}>
                            <label className="Pause-Modal-Label">*Job Log Issue</label>
                            <Dropdown className="Pause-Modal-Dropdown">
                                <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter" disabled={true}>
                                    <span>{issueType === '' ? 'Issue' : issueType}</span>
                                </Dropdown.Toggle>
                            </Dropdown>
                        </Col>
                    ) : (
                        <></>
                    )}
                </Row>

                {[1, 2, 3, 6].includes(logTypeId ?? 0) ? (
                    <Row className="Pause-Modal-Row-Container">
                        <Col md={5}>
                            <div className="Pause-Modal-Input-Container">
                                <label className="Pause-Modal-Label">Estimated Downtime (in minutes)</label>
                                <input
                                    type="number"
                                    className="Pause-Modal-Input"
                                    value={downtime ?? ''}
                                    onChange={event => handleOnChangeDowntime(event)}
                                />
                                <span className="Pause-Modal-Error">{downTimeError}</span>
                            </div>
                        </Col>

                        {stage && (
                            <Col md={5}>
                                <Dropdown className="Pause-Modal-Dropdown">
                                    <Dropdown.Toggle
                                        variant="outline-dark"
                                        id="dropdown-basic"
                                        className="EndStage-wellheadStage"
                                        disabled={true}
                                    >
                                        <span>{stage}</span>
                                    </Dropdown.Toggle>
                                </Dropdown>
                            </Col>
                        )}
                    </Row>
                ) : (
                    <></>
                )}

                {logTypeId === 4 ? (
                    <>
                        <Row className="Pause-Modal-Row-Container">
                            <Col md={5}>
                                <div className="Pause-Modal-Input-Container">
                                    <label className="Pause-Modal-Label">Load ID</label>
                                    <input
                                        type="number"
                                        className="Pause-Modal-Input"
                                        value={loadId ?? ''}
                                        onChange={event => handleOnChangeLoadId(event)}
                                    />
                                    <span className="Pause-Modal-Error">{loadIdError}</span>
                                </div>
                            </Col>
                            <Col md={5}>
                                <Dropdown className="Pause-Modal-Dropdown">
                                    <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter">
                                        <span>{lofa === '' ? 'Lofa/ Location' : lofa}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handleOnChangeLofa(null, '')}>None</Dropdown.Item>
                                        {jobLogLocationLofa.map((locationLofa, index) => (
                                            <Dropdown.Item
                                                key={index}
                                                onClick={() =>
                                                    handleOnChangeLofa(locationLofa.id, locationLofa.descrip)
                                                }
                                            >
                                                {locationLofa.descrip}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Col>
                        </Row>

                        <Row className="Pause-Modal-Row-Container">
                            <Col md={5}>
                                <div className="Pause-Modal-Input-Container">
                                    <label className="Pause-Modal-Label">Amount</label>
                                    <input
                                        type="number"
                                        className="Pause-Modal-Input"
                                        value={amount ?? ''}
                                        onChange={event => handleOnChangeAmount(event)}
                                    />
                                    <span className="Pause-Modal-Error">{amountError}</span>
                                </div>
                            </Col>
                            <Col md={5}>
                                <Dropdown className="Pause-Modal-Dropdown">
                                    <Dropdown.Toggle variant="outline-dark" id="JobLogs-Details-Filter">
                                        <span>{rootCause === '' ? 'Root Cause' : rootCause}</span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handleOnChangeRootCause(null, '')}>
                                            None
                                        </Dropdown.Item>
                                        {jobLogRootCauses.map((rootCause, index) => (
                                            <Dropdown.Item
                                                key={index}
                                                onClick={() => handleOnChangeRootCause(rootCause.id, rootCause.descrip)}
                                            >
                                                {rootCause.descrip}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Col>
                        </Row>

                        <Row className="Pause-Modal-Row-Container">
                            <Col md={10}>
                                <div className="Pause-Modal-Input-Container">
                                    <label className="Pause-Modal-Label">Corrective Action</label>
                                    <input
                                        type="text"
                                        className="Pause-Modal-Input"
                                        value={correctiveAction ?? ''}
                                        onChange={event => handleOnChangeCorrectiveAction(event)}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                ) : (
                    <></>
                )}

                <Row className="Pause-Modal-Row-Container">
                    <Col md={10}>
                        <div className="Pause-Modal-Input-Container">
                            <label className="Pause-Modal-Label">Notes</label>
                            <input
                                type="text"
                                className="Pause-Modal-Input"
                                value={notes ?? ''}
                                onChange={event => handleOnChangeNotes(event)}
                            />
                            <span className="Pause-Modal-Error">{notesError}</span>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer className="AddStageModal-Footer">
                <CustomButton title="Ignore" isBordered onClick={() => onClickIgnore()} />
                {isAddJobLogsEnabled ? (
                    <CustomButton
                        title="Resolve"
                        onClick={() => onClickResolve()}
                        isDisabled={setContinueButtonStatus()}
                    />
                ) : (
                    <CustomButton title="Cancel" isBordered onClick={onClickClose} />
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default StartStageModal;
